import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { formatLocalizedDate } from "@infrastructure/helpers";
import { Box, Divider, Typography, useMediaQuery, useTheme, } from "@mui/material";
import DisplayFormattedMessage from "@ui/components/DisplayFormattedMessage";
import { EText } from "@ui/components/event/typography/EText";
import { HeaderLinks } from "@ui/layout/header/EventHeader";
import React, { useEffect, useMemo, useRef, useState, } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
const MemberBorder = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "start",
    borderLeft: "2px solid #00B6ED",
    paddingLeft: "22px",
    marginTop: "5px",
};
export const DirectorFormSummary = ({ activeStep, watchedValues, eventType, isDrawer = false, }) => {
    const theme = useTheme();
    const personInfo = watchedValues?.PersonalInfo || {};
    const { pickupLocations } = useAppSelector((state) => state.pickupLocation);
    const adultInfo = watchedValues?.AdultInfo || [];
    const childrenInfo = watchedValues?.ChildrenInfo || [];
    const importantInfo = watchedValues?.ImportantInfo || {};
    const accommodation = watchedValues?.Accommodation || {};
    const transport = watchedValues?.Transport || {};
    const { t, i18n } = useTranslation();
    const { singleEvent } = useAppSelector((state) => state.event);
    const { linkVerification } = useAppSelector((state) => state.event);
    const isSmallScreen = useMediaQuery("(max-width:900px)");
    const isBelow1024 = useMediaQuery(theme.breakpoints.down(1025)); // to trigger responsiveness on 1024px
    const isBelow390 = useMediaQuery(theme.breakpoints.down(391)); // to trigger responsiveness on 391px
    const isTablet = useMediaQuery("(min-width:1024px)");
    const isOnly1024 = useMediaQuery(theme.breakpoints.between(1024, 1025));
    const totalAttendees = Number(personInfo.AdultsCount || 0) +
        Number(personInfo.ChildrenCount || 0) +
        1;
    const hasDietary = importantInfo.DietaryRequirements &&
        importantInfo.DietaryRequirements.length > 0;
    const dietryText = importantInfo.DietaryRequirements || "";
    const hasComments = importantInfo.Comments;
    const hasTransport = transport.TransportOption;
    const hasDisability = importantInfo.IsDisability === "true";
    const disabilityComment = importantInfo.DisabilityComments || "";
    const location = useLocation();
    const { invitationDatesForAccommodation, invitationDates } = useAppSelector((state) => state.invitation);
    const isDirectorsPage = location.pathname.includes("directors");
    const dispatch = useAppDispatch();
    const dateOptions = useMemo(() => {
        if (!invitationDatesForAccommodation)
            return [];
        return invitationDatesForAccommodation.map((date) => ({
            label: formatLocalizedDate(date.invitedDate, i18n.language),
            value: date.id,
        }));
    }, [invitationDatesForAccommodation, i18n.language]);
    const accommodationDates = dateOptions
        .filter((option) => watchedValues?.Accommodation?.AccommodationDates?.includes(option.value))
        .map((option) => option.label);
    const transportDates = dateOptions
        .filter((option) => watchedValues?.Transport?.TransportParkingDates?.includes(option.value))
        .map((option) => option.label);
    const attendeesDates = dateOptions
        .filter((option) => {
        return personInfo?.AttendanceDateIds?.includes(option.value);
    })
        .map((option) => option.label);
    const getAccommodationDateRange = (selectedIds) => {
        if (invitationDates && selectedIds.length > 0) {
            const selectedDates = invitationDates
                .filter((date) => selectedIds.includes(date.id))
                .sort((a, b) => new Date(a.invitedDate).getTime() -
                new Date(b.invitedDate).getTime());
            const selectedDatesFormatted = selectedDates
                .map((date) => new Date(date.invitedDate).toLocaleDateString())
                .join(", ");
            return `Your accommodation is booked for the following dates: ${selectedDatesFormatted}.`;
        }
        return "";
    };
    // useEffect(() => {
    //   calculateAge;
    // }, [personInfo]);
    const calculateAge = (dob) => {
        if (!dob)
            return null;
        try {
            const birthDate = new Date(dob);
            // Check if the date is valid
            if (isNaN(birthDate.getTime()))
                return null;
            const today = new Date();
            const diffInMilliseconds = today.getTime() - birthDate.getTime();
            const ageInYears = diffInMilliseconds / (1000 * 60 * 60 * 24 * 365.25);
            return ageInYears < 1
                ? ageInYears.toFixed(1)
                : Math.floor(ageInYears).toString();
        }
        catch (e) {
            return null;
        }
    };
    const getTransportText = (transport) => {
        const count = transport?.NoOfPeople ?? 0;
        const noOfCars = transport?.NoOfCars ?? 0;
        const location = pickupLocations?.find((loc) => loc.id === transport?.PickupLocationId);
        const locationText = location
            ? t("event.summaryTransportOption.location", {
                selectedCompany: location.company,
                address: `${location.businessOrBoardingLocation}, ${location.postalCode} ${location.city}`,
            })
            : "";
        switch (Number(transport?.TransportOption)) {
            case 1: {
                const baseText = count === 1 ? (_jsx(DisplayFormattedMessage, { text: t("event.summaryTransportOption.1", { count }) })) : (_jsx(DisplayFormattedMessage, { text: t("event.summaryTransportOption.1_plural", { count }) }));
                return (_jsxs(_Fragment, { children: [baseText, _jsx("br", {}), _jsx("span", { style: { all: "inherit", fontWeight: 600 }, children: location && _jsx(DisplayFormattedMessage, { text: locationText }) })] }));
            }
            case 2:
                return (_jsxs(_Fragment, { children: [noOfCars === 1
                            ? t("event.summaryTransportOption.2", { noOfCars })
                            : t("event.summaryTransportOption.2_plural", { noOfCars }), "."] }));
            case 3:
                return _jsxs(_Fragment, { children: [t("event.summaryTransportOption.3"), "."] });
            default:
                return null;
        }
    };
    const [isScrollable, setIsScrollable] = useState(false);
    const contentRef = useRef(null);
    // Check if content is scrollable
    const checkIfScrollable = () => {
        if (contentRef.current) {
            const isScrollable = contentRef.current.scrollHeight > contentRef.current.clientHeight;
            setIsScrollable(isScrollable);
        }
    };
    useEffect(() => {
        checkIfScrollable();
    }, [watchedValues]);
    const scrollToBottom = () => {
        if (contentRef.current) {
            contentRef.current.scrollTo({
                top: contentRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    };
    return (
    // Inside return contentRef section only — no need to change useSelector, hooks etc.
    _jsxs(Box, { position: "relative", height: "100%", width: "100%", maxWidth: isBelow390 ? "auto" : isBelow1024 ? 285 : 387, mx: isBelow390 ? "auto" : isBelow1024 ? "auto" : "start", sx: {
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
        }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 5,
                    pl: isOnly1024 ? 3 : 0,
                }, children: [_jsx(Box, { width: "100%", sx: {
                            display: "flex",
                            justifyContent: isBelow1024 ? "center" : "start",
                        }, children: !isDrawer && (_jsx(Box, { component: "img", src: eventType && eventType === "family"
                                ? "/assets/images/swd.png"
                                : "/assets/images/relations-day-logo.svg", sx: {
                                width: "auto",
                                height: `min(${isBelow390 ? 180 : isBelow1024 ? 160 : 260}px, 26.5dvh)`,
                            }, alt: "feature image" })) }), _jsx(Box, { ref: contentRef, sx: {
                            msOverflowStyle: "none",
                            pb: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }, children: Object.keys(watchedValues).length > 0 && (_jsxs(_Fragment, { children: [_jsxs(EText, { variant: isBelow390 ? "subtitle1" : isBelow1024 ? "h6" : "h5", color: "#FFFFFF", fontWeight: 500, children: [t("common.dear"), " ", personInfo.FirstName, " ", personInfo.MiddleName, " ", personInfo.LastName || "", ","] }), _jsxs(Box, { sx: { display: "flex", flexDirection: "column" }, children: [_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.5 }, children: [linkVerification?.data?.user.email && (_jsx(Typography, { variant: "body1", color: "#FFFFFF", children: linkVerification?.data?.user.email })), personInfo?.Phone && (_jsxs(Typography, { variant: "body1", color: "#FFFFFF", children: ["+", personInfo?.Phone] }))] }), _jsxs(Box, { children: [_jsx(Divider, { sx: {
                                                        my: 1.5,
                                                        "&::before, &::after": {
                                                            borderColor: "#FFFFFF1A",
                                                        },
                                                    } }), attendeesDates.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body1", color: "#FFFFFF", children: t("event.attendanceDate") }), _jsx(Typography, { variant: "body2", color: "#FFFFFF", children: _jsx(DisplayFormattedMessage, { text: attendeesDates
                                                                    .map((date) => `- ${date}`)
                                                                    .join("\n") }) }), _jsx(Divider, { sx: {
                                                                my: 1.5,
                                                                "&::before, &::after": {
                                                                    borderColor: "#FFFFFF1A",
                                                                },
                                                            } })] }))] }), _jsx(Box, { children: personInfo?.Gender && personInfo?.ShirtSize && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body1", color: "#FFFFFF", children: t("formFields.selectedShirt") }), _jsx(Typography, { variant: "body2", color: "#FFFFFF", children: _jsx(DisplayFormattedMessage, { text: t("formFields.modelAndSize", {
                                                                shirtModel: t(`event.gender.${personInfo.Gender}`),
                                                                shirtSize: personInfo.ShirtSize,
                                                            }) }) }), _jsx(Divider, { sx: {
                                                            my: 1.5,
                                                            "&::before, &::after": {
                                                                borderColor: "#FFFFFF1A",
                                                            },
                                                        } })] })) }), _jsx(Box, { sx: {
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.5,
                                                whiteSpace: "pre-line",
                                            }, children: accommodationDates.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body1", color: "#FFFFFF", children: t("event.overnightDates") }), _jsx(Typography, { variant: "body2", color: "#FFFFFF", children: _jsx(DisplayFormattedMessage, { text: accommodationDates
                                                                .map((date) => `- ${date}`)
                                                                .join("\n") }) }), _jsx(Divider, { sx: {
                                                            my: 1.5,
                                                            "&::before, &::after": {
                                                                borderColor: "#FFFFFF1A",
                                                            },
                                                        } })] })) }), _jsx(Box, { sx: {
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.5,
                                                whiteSpace: "pre-line",
                                            }, children: transportDates.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body1", color: "#FFFFFF", children: t("event.transportDates") }), _jsx(Typography, { variant: "body2", color: "#FFFFFF", children: _jsx(DisplayFormattedMessage, { text: transportDates
                                                                .map((date) => `- ${date}`)
                                                                .join("\n") }) }), _jsx(Divider, { sx: {
                                                            my: 1.5,
                                                            "&::before, &::after": {
                                                                borderColor: "#FFFFFF1A",
                                                            },
                                                        } })] })) }), hasTransport && (_jsxs(React.Fragment, { children: [_jsx(Typography, { variant: "body1", color: "#FFFFFF", children: getTransportText(transport) }), pickupLocations?.find((loc) => loc.id === transport?.PickupLocationId) && (_jsx(Divider, { sx: {
                                                        my: 1.5,
                                                        "&::before, &::after": {
                                                            borderColor: "#FFFFFF1A",
                                                        },
                                                    } }))] })), _jsx(Box, { sx: {
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.5,
                                                whiteSpace: "pre-line",
                                            }, children: hasDisability && disabilityComment.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle1", color: "#FFFFFF", children: t("event.disabilityCommentsHeading") }), _jsx(Typography, { variant: "body2", color: "#FFFFFF", children: `- ${disabilityComment}` })] })) }), _jsx(Box, { sx: {
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.5,
                                                whiteSpace: "pre-line",
                                            }, children: hasDietary && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle1", color: "#FFFFFF", children: t("event.dietryCommentsHeading") }), _jsx(Typography, { variant: "body2", color: "#FFFFFF", children: `- ${dietryText}` })] })) }), hasComments && (_jsxs(React.Fragment, { children: [_jsx(Typography, { variant: "subtitle1", color: "#FFFFFF", children: t("event.additionalCommentsHeading") }), _jsx(Typography, { variant: "body2", color: "#FFFFFF", children: `- ${hasComments}` }), _jsx(Divider, { sx: {
                                                        my: 1,
                                                        "&::before, &::after": {
                                                            borderColor: "#FFFFFF1A",
                                                        },
                                                    } })] }))] })] })) })] }), isSmallScreen && (_jsx(Box, { sx: {
                    paddingTop: 6,
                }, children: _jsx(HeaderLinks, {}) }))] }));
};
